import { mark } from './boot-trace'; // first: its module body stamps the first JS mark
import { render } from 'preact';
import { art, loadArtManifest } from './art';
import './styles/app.css';
import { App } from './app';
import { principal, balance, serverOffset, pub, priv, seatOption } from './state/store';
import { initCutHover } from './ui/hover-cut';

mark('main'); // every module evaluated
await loadArtManifest();
// CSS image URLs must be resolved after the manifest arrives, before the first render.
const cssArt = {
  '--art-login2-pill-v1-2x-webp': art('/art/login2/pill-v1@2x.webp'),
  '--art-ui-extra-banner-blue-png': art('/art/ui-extra/banner-blue.png'),
  '--art-ui-extra-navy-pill-png': art('/art/ui-extra/navy-pill.png'),
  '--art-ui-extra-note-plate-png': art('/art/ui-extra/note-plate.png'),
  '--art-ui-extra-rule-png': art('/art/ui-extra/rule.png'),
  '--art-ui-extra-scrim-png': art('/art/ui-extra/scrim.png'),
  '--art-ui-extra-toast-plate-png': art('/art/ui-extra/toast-plate.png'),
  '--art-ui-chip-dark-webp': art('/art/ui/chip-dark.webp'),
  '--art-ui-line-disc-noway-v1-2x-webp': art('/art/ui/line/disc-noway-v1@2x.webp'),
  '--art-ui-line-disc-v1-2x-webp': art('/art/ui/line/disc-v1@2x.webp'),
  '--art-ui-line-disc-way-v1-2x-webp': art('/art/ui/line/disc-way-v1@2x.webp'),
  '--art-ui-line-socket-on-v1-2x-webp': art('/art/ui/line/socket-on-v1@2x.webp'),
  '--art-ui-line-socket-v1-2x-webp': art('/art/ui/line/socket-v1@2x.webp'),
  '--art-ui-panel-cyan-webp': art('/art/ui/panel-cyan.webp'),
  '--art-ui-panel-game-frame-webp': art('/art/ui/panel-game-frame.webp'),
  '--art-ui-panel-gold-webp': art('/art/ui/panel-gold.webp'),
  '--art-ui-panel-pink-webp': art('/art/ui/panel-pink.webp'),
  '--art-ui-panel-sheet-webp': art('/art/ui/panel-sheet.webp'),
  '--art-ui-row-step-normal-webp': art('/art/ui/row-step-normal.webp'),
  '--art-ui-row-step-noway-webp': art('/art/ui/row-step-noway.webp'),
  '--art-ui-row-step-way-webp': art('/art/ui/row-step-way.webp'),
  '--art-ui-sheet-qr-v1-2x-webp': art('/art/ui/sheet-qr-v1@2x.webp'),
  '--art-ui-sheet-row-selected-v1-2x-webp': art('/art/ui/sheet-row-selected-v1@2x.webp'),
  '--art-ui-sheet-row-v1-2x-webp': art('/art/ui/sheet-row-v1@2x.webp'),
  '--art-ui-stage-glow-2x-png': art('/art/ui/stage-glow@2x.png'),
  '--art-ui4-chip-gold-filled-2x-webp': art('/art/ui4/chip-gold-filled@2x.webp'),
  '--art-ui4-pill-blue-2x-webp': art('/art/ui4/pill-blue@2x.webp'),
  '--art-ui4-pill-gold-2x-webp': art('/art/ui4/pill-gold@2x.webp'),
  '--art-ui4-pill-pink-2x-webp': art('/art/ui4/pill-pink@2x.webp'),
  '--art-ui4b-btn-danger-2x-webp': art('/art/ui4b/btn-danger@2x.webp'),
  '--art-ui4b-btn-ghost-2x-webp': art('/art/ui4b/btn-ghost@2x.webp'),
  '--art-ui4b-btn-primary-2x-webp': art('/art/ui4b/btn-primary@2x.webp'),
  '--art-ui4b-btn-secondary-2x-webp': art('/art/ui4b/btn-secondary@2x.webp'),
  '--art-ui4b-panel-join-spent-2x-webp': art('/art/ui4b/panel-join-spent@2x.webp'),
  '--art-ui4b-panel-legal-2x-webp': art('/art/ui4b/panel-legal@2x.webp'),
  '--art-ui4b-pill-lang-2x-webp': art('/art/ui4b/pill-lang@2x.webp'),
  '--art-ui4b-sheet-panel-2x-webp': art('/art/ui4b/sheet-panel@2x.webp'),
  '--art-ui4b-sheet-row-danger-2x-webp': art('/art/ui4b/sheet-row-danger@2x.webp'),
  '--art-ui4b-sheet-row-2x-webp': art('/art/ui4b/sheet-row@2x.webp'),
  '--art-ui4b-toggle-off-2x-webp': art('/art/ui4b/toggle-off@2x.webp'),
  '--art-ui4b-toggle-on-2x-webp': art('/art/ui4b/toggle-on@2x.webp'),
  '--art-lobby-kit-cta-plate-png': art('/art/lobby-kit/cta.plate.png'),   // the game's gold plate, used by the Profile CONTINUE
  // the shared surfaces the Profile page is assembled from (Codex's spec, art/src/profile-2026-09-25)
  '--art-ui4-panel-meetflirt-2x-png': art('/art/ui4/panel-meetflirt@2x.png'),
  '--art-ui-field-v1-2x-png': art('/art/ui/field-v1@2x.png'), '--art-ui-field-focus-v1-2x-png': art('/art/ui/field-focus-v1@2x.png'),   // the profile's new fields (7.10.2026)
  '--art-ui4-pill-blue-2x-png': art('/art/ui4/pill-blue@2x.png'),
  '--art-ui4-pill-pink-2x-png': art('/art/ui4/pill-pink@2x.png'),
  '--art-ui4-tile-avatar-2x-png': art('/art/ui4/tile-avatar@2x.png'),
  '--art-ui4-tile-avatar-on-2x-png': art('/art/ui4/tile-avatar-on@2x.png'),
  '--art-popup-pill-large-v2-png': art('/art/popup/slice/pill.large.v2.png'),
  '--art-ui4b-cell-code-idle-2x-png': art('/art/ui4b/cell-code-idle@2x.png'),
  '--art-ui4b-cell-code-on-2x-png': art('/art/ui4b/cell-code-on@2x.png'),
  '--art-lobby-kit-small-button-png': art('/art/lobby-kit/small.button.png'),
  '--art-lobby-kit-pill-large-png': art('/art/lobby-kit/pill.large.png'),
  '--art-lobby-kit-row-divider-png': art('/art/lobby-kit/row.divider.png'),   // the drawn hairline between list rows
  '--art-popup-panel9-png': art('/art/popup/slice/panel9.png'),
  // the game's scrollbar: two 3-slice pieces, the groove and the capsule (art/src/scroll-2026-09-26)
  '--art-scroll-track-png': art('/art/scroll/slice/track.png'),
  '--art-scroll-thumb-png': art('/art/scroll/slice/thumb.png'),
};
for (const [name, url] of Object.entries(cssArt)) {
  document.documentElement.style.setProperty(name, `url(${JSON.stringify(url)})`);
}
initCutHover();
requestAnimationFrame(() => mark('f1')); // first frame callback (registered before the app renders)
render(<App />, document.getElementById('app')!);
// A read-only window of the live state for var/tools/capture-views.mjs, which records every game view as a frozen fixture
// for /views (owner 22.9.2026: "Miten saan nuo näkymät pysäytettyyn moodiin jotta voin itse käydä ne läpi"). Own data only.
(window as unknown as { __snw: unknown }).__snw = { get state() { return { capturedAt: Date.now(), principal: principal.value, balance: balance.value, serverOffset: serverOffset.value, pub: pub.value, priv: priv.value, seatOption: seatOption.value }; } };
mark('rendered');
// boot-frame attribution (incident 9.9.2026): f1b = same frame after render, f2 = next frame (= first frame's style/layout/paint done), t0 = first timer
requestAnimationFrame(() => { mark('f1b'); requestAnimationFrame(() => mark('f2')); }); setTimeout(() => mark('t0'), 0);
